import { V1_PRIMITIVE_TO_V2_PRIMITIVE, V1_SEMANTIC_TO_V2_SEMANTIC } from './v1-to-v2-color-mapping-data';
import type { V1PrimitiveTokenKey, V1SemanticTokenKey, V2MappedColorToken, V2MappedPrimitiveToken, V2MappedSemanticToken } from './v1-to-v2-color-mapping-data';
export type { V1PrimitiveTokenKey, V1SemanticTokenKey, V2MappedColorToken, V2MappedPrimitiveToken, V2MappedSemanticToken, V2PrimitiveTokenKey, V2SemanticTokenKey } from './v1-to-v2-color-mapping-data';
export { V1_PRIMITIVE_TO_V2_PRIMITIVE, V1_SEMANTIC_TO_V2_SEMANTIC } from './v1-to-v2-color-mapping-data';
const lookup = <T extends Record<string, string>,>(map: T, key: string): T[keyof T] | undefined => key in map ? map[(key as keyof T)] : undefined;

/** Map a Base 1.0 primitive token key to its v2 primitive equivalent. */
export const mapV1PrimitiveToV2 = (v1Token: V1PrimitiveTokenKey): V2MappedPrimitiveToken | undefined => lookup(V1_PRIMITIVE_TO_V2_PRIMITIVE, v1Token);

/** Map a Base 1.0 semantic token key to its v2 semantic equivalent. */
export const mapV1SemanticToV2 = (v1Token: V1SemanticTokenKey | string): V2MappedSemanticToken | undefined => lookup(V1_SEMANTIC_TO_V2_SEMANTIC, v1Token);

/**
 * Map a Base 1.0 color token key to its v2 primitive or semantic equivalent.
 * Primitives are checked first, then semantics.
 */
export const mapV1ColorTokenToV2 = (v1Token: string): V2MappedColorToken | undefined => mapV1PrimitiveToV2((v1Token as V1PrimitiveTokenKey)) ?? mapV1SemanticToV2(v1Token);

/**
 * Convert a v2 primitive camelCase key to its CSS variable suffix.
 * e.g. amber96 → amber-96, gradientBlue0 → gradient-blue-0
 */
const v2PrimitiveKeyToCssSegment = (key: string): string => key.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/([a-zA-Z])(\d)/g, '$1-$2').toLowerCase();

/**
 * Tailwind color map entries for v1 primitive keys that cross-map to a
 * different v2 primitive (e.g. amber100 → amber96). Add these to
 * baseTailwindConfigV2 so that classes like bg-amber100 switch between V1 and
 * V2 values based on [data-base-version-color="2"].
 *
 * Solid primitives use the rgb(var(--tw-compat-*) / <alpha-value>) form so
 * that the value toggles between V1 and V2 when the color version flag changes.
 * Gradient primitives (which carry embedded alpha) fall back to var(--base-*).
 *
 * Requires base-colors-v2-tw.css to be loaded (it defines both --tw-base-* and
 * --tw-compat-* in :root, with --tw-compat-* overridden under [data-base-version-color="2"]).
 */
export const getV1PrimitiveTailwindV2Colors = (): Record<string, string> => Object.fromEntries(Object.entries(V1_PRIMITIVE_TO_V2_PRIMITIVE).map(([v1Key, v2Key]) => {
  const value = v2Key.startsWith('gradient') ? `var(--base-${v2PrimitiveKeyToCssSegment(v2Key)})` : `rgb(var(--tw-compat-${v1Key}) / <alpha-value>)`;
  return [v1Key, value];
}));